import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import Header from './Header';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptIcon from '@mui/icons-material/Receipt';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  rejected: 'bg-red-100 text-red-800'
};

const statusIcons = {
  processing: <AssignmentIcon className="!w-5 !h-5" />,
  confirmed: <CheckCircleIcon className="!w-5 !h-5" />,
  shipped: <LocalShippingIcon className="!w-5 !h-5" />,
  delivered: <CheckCircleIcon className="!w-5 !h-5" />,
  cancelled: <CancelIcon className="!w-5 !h-5" />,
  rejected: <CancelIcon className="!w-5 !h-5" />
};

const CustomerOrderDetails = () => {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTracking, setShowTracking] = useState(false);

  const loadOrder = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (orderSnap.exists()) {
        setOrder({
          id: orderSnap.id,
          ...orderSnap.data()
        });
      } else {
        setError(t('customerOrder.orderNotFound'));
      }
    } catch (err) {
      console.error('Error loading order:', err);
      setError(t('customerOrder.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [orderId, t]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  // Embed the tracking.my courier status only once the user opens it
  useEffect(() => {
    const trackingNumber = order?.trackingNumber;
    if (!trackingNumber || !showTracking) return;

    const SCRIPT_SRC = 'https://www.tracking.my/track-button.js';

    const embed = () => {
      const container = document.getElementById('embedTrack');
      if (!window.TrackButton || !container) return;
      container.innerHTML = '';
      window.TrackButton.embed({
        selector: '#embedTrack',
        tracking_number: trackingNumber
      });
    };

    if (window.TrackButton) {
      embed();
      return;
    }

    let script = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener('load', embed);
    return () => script.removeEventListener('load', embed);
  }, [order?.trackingNumber, showTracking]);

  const formatDate = (date) => {
    if (!date) return t('customerOrder.notAvailable', 'N/A');
    
    // Handle Firebase Timestamp
    let dateObj;
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      dateObj = new Date(date);
    }
    
    return dateObj.toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount);
  };

  const handleViewInvoice = () => {
    if (!order) {
      alert(t('customerOrder.orderInfoUnavailable'));
      return;
    }
    navigate(`/invoice/${order.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-20">
          <div className="flex-1 min-w-0 flex items-center justify-center min-h-[calc(100vh-80px)]">
            <div className="text-center text-gray-500 text-xl">{t('customerOrder.loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-20">
          <div className="flex-1 min-w-0 flex items-center justify-center min-h-[calc(100vh-80px)]">
            <div className="text-center text-red-500 text-xl">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-20">
          <div className="flex-1 min-w-0 flex items-center justify-center min-h-[calc(100vh-80px)]">
            <div className="text-center text-gray-500 text-xl">{t('customerOrder.orderNotFound')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex-1 pt-20">
        <div className="flex-1 min-w-0">
          <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center text-primary font-semibold hover:underline mb-6"
          >
            <ArrowBackIcon className="mr-2 !w-5 !h-5" />
            {t('customerOrder.backToOrders')}
          </button>

          {/* Order Header */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="bg-primary text-white px-6 py-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h1 className="text-2xl font-bold flex items-center">
                    <AssignmentIcon className="mr-3 !w-6 !h-6" />
                    {t('customerOrder.orderDetails')}
                  </h1>
                  <p className="text-sm mt-1 text-blue-100">{t('customerOrder.orderId')}: {order.orderId || order.id}</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {order.status === 'shipped' && (
                    <button
                      onClick={handleViewInvoice}
                      className="flex items-center gap-2 bg-white text-primary px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
                    >
                      <ReceiptIcon className="!w-5 !h-5" />
                      {t('customerOrder.viewInvoice')}
                    </button>
                  )}
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                    {statusIcons[order.status] || <AssignmentIcon className="!w-4 !h-4 mr-1" />}
                    <span className="ml-1">
                      {order.status
                        ? t(`customerOrder.status.${order.status}`, order.status.charAt(0).toUpperCase() + order.status.slice(1))
                        : ''}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <strong className="text-gray-700">{t('customerOrder.orderDate')}:</strong>
                  <p className="text-gray-900">{formatDate(order.dateTime || order.createdAt)}</p>
                </div>
                <div>
                  <strong className="text-gray-700">{t('customerOrder.totalAmount')}:</strong>
                  <p className="text-primary font-bold text-lg">{formatCurrency(order.totalAmount || order.total)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-xl font-bold text-primary flex items-center">
                <PersonIcon className="mr-2 !w-6 !h-6" />
                {t('customerOrder.customerInformation')}
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center">
                  <PersonIcon className="!w-5 !h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">{t('customerOrder.name')}</p>
                    <p className="font-medium">{order.purchaserName}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <PhoneIcon className="!w-5 !h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">{t('customerOrder.phoneNumber')}</p>
                    <p className="font-medium">{order.purchaserPhoneNumber}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <LocationOnIcon className="!w-5 !h-5 text-gray-400 mr-3 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">{t('customerOrder.deliveryAddress')}</p>
                    {typeof order.purchaserAddress === 'object' ? (
                      <div className="bg-gray-50 p-3 rounded border border-gray-200">
                        <p className="font-medium">{order.purchaserAddress.line1}</p>
                        {order.purchaserAddress.line2 && (
                          <p className="font-medium">{order.purchaserAddress.line2}</p>
                        )}
                        <p>{order.purchaserAddress.city}</p>
                        <p>{order.purchaserAddress.postcode} {order.purchaserAddress.state}</p>
                      </div>
                    ) : (
                      <p className="font-medium">{order.purchaserAddress}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-xl font-bold text-primary">{t('customerOrder.orderItems')}</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {order.products?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.productName || item.name}</p>
                      {item.sku && <p className="text-sm text-gray-600">{t('customerOrder.sku')}: {item.sku}</p>}
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-700">{t('customerOrder.quantity')}: {item.quantity}</p>
                      {item.amount !== undefined && (
                        <p className="text-sm text-gray-600">{t('customerOrder.unitPrice')}: {formatCurrency(item.amount)}</p>
                      )}
                      {item.totalAmount !== undefined && (
                        <p className="text-sm font-semibold text-primary">{t('customerOrder.total')}: {formatCurrency(item.totalAmount)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Order Summary */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">{t('customerOrder.subtotal')}:</span>
                  <span className="font-medium">{formatCurrency(order.amount || 0)}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">{t('customerOrder.shippingFee')}:</span>
                  <span className="font-medium">
                    {order.shippingFees === 0 ? t('customerOrder.free') : formatCurrency(order.shippingFees || 0)}
                  </span>
                </div>
                {order.totalWeight && (
                  <div className="flex justify-between items-center mb-2 text-sm text-gray-500">
                    <span>{t('customerOrder.totalWeight')}:</span>
                    <span>{order.totalWeight} kg</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-gray-200 mt-3">
                  <span className="text-lg font-bold text-gray-900">{t('customerOrder.totalAmount')}:</span>
                  <span className="text-xl font-bold text-primary">{formatCurrency(order.totalAmount || order.total || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping Information (if shipped) */}
          {(order.courier || order.trackingNumber || order.invoiceNumber) && (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h2 className="text-xl font-bold text-primary flex items-center">
                  <LocalShippingIcon className="mr-2 !w-6 !h-6" />
                  {t('customerOrder.shippingInformation')}
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {order.courier && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">{t('customerOrder.courier')}:</p>
                      <p className="text-gray-900 text-lg">{order.courier}</p>
                    </div>
                  )}
                  {order.trackingNumber && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">{t('customerOrder.trackingNumber')}:</p>
                      <p className="text-gray-900 font-mono bg-blue-50 p-2 rounded border border-blue-200 text-lg">
                        {order.trackingNumber}
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowTracking((prev) => !prev)}
                        className="mt-3 inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-900 transition-colors shadow-md"
                      >
                        <LocalShippingIcon className="!w-5 !h-5" />
                        {showTracking ? t('customerOrder.hideCourierStatus') : t('customerOrder.showCourierStatus')}
                      </button>
                      {showTracking && <div id="embedTrack" className="mt-3" />}
                    </div>
                  )}
                  {order.invoiceNumber && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">{t('customerOrder.invoiceNumber')}:</p>
                      <p className="text-gray-900 font-mono bg-green-50 p-2 rounded border border-green-200 text-lg">
                        {order.invoiceNumber}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Rejection Information (if rejected) */}
          {order.rejectionReason && (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
              <div className="px-6 py-4 bg-red-50 border-b border-red-200">
                <h2 className="text-xl font-bold text-red-800 flex items-center">
                  <CancelIcon className="mr-2 !w-6 !h-6" />
                  Order Rejection Notice
                </h2>
              </div>
              <div className="p-6">
                <p className="text-gray-700 bg-red-50 p-4 rounded border border-red-200">
                  {order.rejectionReason}
                </p>
              </div>
            </div>
          )}

          {/* Payment Information */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-xl font-bold text-primary flex items-center">
                <PaymentIcon className="mr-2 !w-6 !h-6" />
                Payment Information
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Payment Status:</p>
                  <p className="text-gray-900">Receipt Uploaded</p>
                </div>
                {order.receiptUrl && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Payment Receipt:</p>
                    <img 
                      src={order.receiptUrl} 
                      alt="Payment Receipt" 
                      className="max-w-full h-auto rounded border border-gray-200 cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => window.open(order.receiptUrl, '_blank')}
                    />
                    <p className="text-xs text-gray-500 mt-1">Click to view full size</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerOrderDetails;

